// src/features/audittrail/ComplianceTab.tsx
// Generate and retrieve reports for a regulator. ListComplianceReports,
// GenerateComplianceReport, GetComplianceReport.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Badge, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import type { ComplianceReport } from '@core/types/audittrail';
import { GenerateReportDialog } from './GenerateReportDialog';
import { enumKey, fmtDate, errMessage } from './logic';

export const ComplianceTab = observer(() => {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<ComplianceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [refreshing, setRefreshing] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    audittrailStore.ListComplianceReports()
      .then((res) => setItems(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('audittrail.compliance.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (row: ComplianceReport) => {
    if (!row.entity?.id) return;
    setRefreshing(row.entity.id);
    try {
      const res = await audittrailStore.GetComplianceReport(row.entity.id);
      setItems((prev) => prev.map((r) => (r.entity?.id === row.entity?.id ? (res.report ?? r) : r)));
    } catch {
      // leave as-is
    } finally {
      setRefreshing(null);
    }
  };

  const statusVariant = (s?: string) => (s === 'COMPLIANCE_REPORT_STATUS_READY' ? 'success' : s === 'COMPLIANCE_REPORT_STATUS_FAILED' ? 'error' : 'secondary');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('audittrail.compliance.subtitle')}</p>
        <Button size="sm" onClick={() => setFormOpen(true)}>{t('audittrail.compliance.generate')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <Table hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('audittrail.compliance.column.name')}</TableHead>
            <TableHead>{t('audittrail.compliance.column.type')}</TableHead>
            <TableHead>{t('audittrail.compliance.column.period')}</TableHead>
            <TableHead>{t('audittrail.compliance.column.status')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('audittrail.compliance.empty')} />
          ) : (
            items.map((r) => (
              <TableRow key={r.entity?.id}>
                <TableCell className="text-sm">{r.name}</TableCell>
                <TableCell className="text-sm">{t(`audittrail.complianceReportType.${enumKey(r.report_type, 'COMPLIANCE_REPORT_TYPE_')}`)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(r.period_start as unknown as string, locale)} – {fmtDate(r.period_end as unknown as string, locale)}</TableCell>
                <TableCell><Badge variant={statusVariant(r.status)} size="sm">{t(`audittrail.complianceReportStatus.${enumKey(r.status, 'COMPLIANCE_REPORT_STATUS_')}`)}</Badge></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    {r.status === 'COMPLIANCE_REPORT_STATUS_READY' && r.download_url ? (
                      <a href={r.download_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">{t('audittrail.compliance.download')}</a>
                    ) : (
                      <Button size="sm" variant="ghost" isLoading={refreshing === r.entity?.id} onClick={() => refresh(r)}>{t('audittrail.compliance.refresh')}</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <GenerateReportDialog open={formOpen} onClose={() => setFormOpen(false)} onGenerated={load} />
    </div>
  );
});

export default ComplianceTab;
